import { useEffect, useState } from 'react';
import { listStoneSuppliers, createStoneSupplier, updateStoneSupplier, deactivateStoneSupplier, formatApiError } from '@/utils/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import StonesHeader from '@/components/stones/StonesHeader';
import { Plus, Trash2, Users, Loader2, Pencil } from 'lucide-react';

const BLANK_FORM = { name: '', email: '', password: '', company_name: '', contact_phone: '', notes: '' };

export default function StoneSuppliersAdminPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);  // supplier doc being edited, or null

  const fetch = async () => {
    try {
      const res = await listStoneSuppliers();
      setSuppliers(res.data.data || []);
    } catch (e) { toast.error('Failed to load suppliers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setSaving(true);
    try {
      await createStoneSupplier(form);
      toast.success('Supplier created');
      setForm(BLANK_FORM);
      setShowForm(false);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create supplier'));
    } finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Deactivate ${name}? They keep historical quotes but can't log in.`)) return;
    try {
      await deactivateStoneSupplier(id);
      toast.success('Supplier deactivated');
      fetch();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <CollapsibleLayout>
      <StoneTabs active="suppliers" />
      <StonesHeader icon={Users} title="Stone Suppliers" subtitle={`${suppliers.length} on file`}>
        <Button onClick={() => setShowForm(!showForm)} className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="add-supplier-btn">
          <Plus size={13} className="mr-1" /> Add Supplier
        </Button>
      </StonesHeader>
      <div className="max-w-[1200px] mx-auto p-4 sm:p-6 space-y-4">

        {showForm && (
          <form onSubmit={submit} className="p-4 bg-white rounded-lg border space-y-3" data-testid="add-supplier-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm mt-1" data-testid="supplier-name" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm mt-1" data-testid="supplier-email" /></div>
              <div><Label className="text-xs">Password *</Label><Input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="h-9 text-sm mt-1" data-testid="supplier-password" /></div>
              <div><Label className="text-xs">Company</Label><Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="h-9 text-sm mt-1" /></div>
              <div><Label className="text-xs">Phone <span className="text-emerald-600">(used for WhatsApp)</span></Label><Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="h-9 text-sm mt-1" placeholder="10-digit or with country code" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-9 text-sm mt-1" /></div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="save-supplier-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Save
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setShowForm(false); setForm(BLANK_FORM); }}>Cancel</Button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border">
            <Users size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">No suppliers yet. Add your first one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`supplier-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{s.email}</td>
                    <td className="px-3 py-2">{s.company_name || '—'}</td>
                    <td className="px-3 py-2">{s.contact_phone || <span className="text-amber-600 text-[11px]">no phone</span>}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${s.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{s.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditing(s)} data-testid={`edit-${s.id}`}>
                        <Pencil size={13} className="mr-1" /> Edit
                      </Button>
                      {s.status === 'approved' && (
                        <Button variant="ghost" size="sm" className="text-red-500 h-7 w-7 p-0 ml-1" onClick={() => remove(s.id, s.name)} data-testid={`deactivate-${s.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditSupplierDialog
        supplier={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetch(); }}
      />
    </CollapsibleLayout>
  );
}

// Dialog for editing supplier profile. Empty fields are not sent — admin can
// edit just the phone (e.g.) without re-entering the rest. Password is opt-in.
function EditSupplierDialog({ supplier, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (supplier) {
      setForm({
        name: supplier.name || '',
        email: supplier.email || '',
        password: '',
        company_name: supplier.company_name || '',
        contact_phone: supplier.contact_phone || '',
        notes: supplier.notes || '',
      });
    }
  }, [supplier]);

  if (!supplier) return null;

  const submit = async () => {
    setSaving(true);
    try {
      // Only send fields that differ from current values (and always send password if set)
      const payload = {};
      for (const k of ['name', 'email', 'company_name', 'contact_phone', 'notes']) {
        if ((form[k] || '') !== (supplier[k] || '')) payload[k] = form[k];
      }
      if (form.password && form.password.trim()) payload.password = form.password.trim();
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }
      await updateStoneSupplier(supplier.id, payload);
      toast.success('Supplier updated');
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!supplier} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit supplier: {supplier.name}</DialogTitle>
          <DialogDescription>
            Update profile fields. Leave password blank to keep current password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">New password <span className="text-muted-foreground">(leave blank to keep current)</span></Label>
            <Input type="text" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="h-9 mt-1" placeholder="—" />
          </div>
          <div>
            <Label className="text-xs">Company</Label>
            <Input value={form.company_name || ''} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Phone <span className="text-emerald-600">(used for WhatsApp notify)</span></Label>
            <Input value={form.contact_phone || ''} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="h-9 mt-1" placeholder="10-digit or with country code" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-9 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
